import { type NetPriceData, type PriceData } from "~/domains/pricing/types/PriceData";
import { type WeightData } from "~/domains/product/types/WeightData";
import { type StockLevel } from "~/domains/product/components/StockIndicator/StockIndicator";
import { type LineItemMessage } from "../../../../utils/cartUpdating/common";
import { type StockData } from "~/utils/cartUpdating/delivery/stockDataTypes";
import type { PurchasePlace, PurchaseType } from "~/app/analytics/ga/recommendations/types";
import type {
  EntryGroup,
  OrderableGroup,
  QuotableGroup,
  BundleQuoteGroup,
  ProductPhaseoutGroup,
  PackPhaseoutGroup,
  ProductGroup,
  KitGroup,
} from "../dto/CartResponseDTO";

export enum PackageEntrySource {
  DEFAULT = "DEFAULT",
  BULK = "BULK",
  QUOTE = "QUOTE",
  CONTRACT = "CONTRACT",
}

export interface EstimatedDeliveryDate {
  type: "estimate" | "replenishment";
  date: Date;
}

export interface LineItem {
  pk: number;
  /** Alias for `pk`; assigned by the transformer as `id = dto.pk`. */
  id: number;
  /**
   * PK of the parent `AbstractOrderEntry` (one level above the package entry).
   * Sent to the backend as `orderEntryPk` in cart and quote update payloads.
   */
  cartEntryPk: number;
  productCode: string;
  entryNumber: number | null;
  /** IDs of all ancestor EntryGroups from root down to the innermost group. */
  groupNumbers: number[];
  quantity: number;
  packSize?: number;
  packCode: string;
  deliveryDate: Date | null;
  manuallySetRDD: boolean;
  weight: WeightData;
  comment?: string;
  valid: boolean;
  primaryWarehouseName: string;
  primaryWarehouseCode: string;
  netPrice?: NetPriceData;
  messages: LineItemMessage[];
  contractErrorMessage?: LineItemMessage;
  disableEdit: boolean;
  countryOfOrigin: string;
  tariffSurcharge: boolean;

  // Line item type as provided by BE
  source: PackageEntrySource;

  minRdd?: Date;
  maxRdd?: Date;

  showComments: boolean;
  priceReference: string;

  // Salesman specific fields
  internalComment: string;
  /**
   * This field is an exception - this price is NOT per piece, but per {LineItem#priceUnit} pieces
   */
  offerPrice?: PriceData;
  priceOverriden: boolean;
  targetPrice?: PriceData;
  /**
   * Minimum Sale Price per single piece of product. Even when priceUnit !== 1
   * TODO: Should be price per {priceUnit} pieces
   */
  msp?: PriceData;
  /**
   * Recommended Sale Price per single piece of product. Even when priceUnit !== 1
   * TODO: Should be price per {priceUnit} pieces
   */
  rsp?: PriceData;

  // Contract specific fields
  contractNumber: string;
  contractLine: string;

  // Quote specific fields
  quoteNumber: string;
  quoteLine: string;
  minimumQty: number;
  isBundled?: boolean;
  original: boolean;

  estimatedShippingDate: Date | null;
  logisticalComment?: string;
  quoteExpiryDate: Date | null;

  // Fields not sent to hybris - filled based on Availability data
  edd: EstimatedDeliveryDate | null;
  isInStock: StockLevel | null;

  // UI Fields - also not sent to hybris
  isPackagingSelectionOpen: boolean; // Show packaging selection in Distributor variant

  // Disables the option of changing package
  restrictedPackage: boolean;

  customerOrderNumber: string;

  partialDeliveries?: StockData;

  // Sales push fields
  saleCampaignCode?: string;
  actualSalePrice?: PriceData;
  actualNetPrice?: PriceData;
  saleCampaignEnabled?: boolean;

  // Product Recommendation fields
  recoPlace?: PurchasePlace;
  recoType?: PurchaseType;

  // Switch to newest product version
  successorProductCode?: string;
}

// ── Group tree types (re-exported from DTO for single-import convenience) ────

export type {
  EntryGroup,
  EntryGroupType,
  OrderableGroup,
  QuotableGroup,
  BundleQuoteGroup,
  ProductPhaseoutGroup,
  PackPhaseoutGroup,
  ProductGroup,
  KitGroup,
} from "../dto/CartResponseDTO";

// ── Type guards ───────────────────────────────────────────────────────────────

export function isOrderableGroup(g: EntryGroup): g is OrderableGroup {
  return g.groupType === "ORDERABLE";
}

export function isQuotableGroup(g: EntryGroup): g is QuotableGroup {
  return g.groupType === "QUOTABLE";
}

export function isBundleQuoteGroup(g: EntryGroup): g is BundleQuoteGroup {
  return g.groupType === "BUNDLE_QUOTE";
}

export function isProductPhaseoutGroup(g: EntryGroup): g is ProductPhaseoutGroup {
  return g.groupType === "PRODUCT_PHASEOUT";
}

export function isPackPhaseoutGroup(g: EntryGroup): g is PackPhaseoutGroup {
  return g.groupType === "PACK_PHASEOUT";
}

export function isProductGroup(g: EntryGroup): g is ProductGroup {
  return g.groupType === "PRODUCT";
}

export function isKitGroup(g: EntryGroup): g is KitGroup {
  return g.groupType === "KIT";
}
